import React, { useCallback, useLayoutEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { type SafeAreaViewProps } from 'react-native-safe-area-context';

import {
  DisplayText,
  theme,
  useSafeExpoNavigation,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { StripeCustomerSheet } from '@order/integrations/stripe';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { PageWrapper, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentsScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const routeParams = useSafeRouteParams<{ redirectTo?: string }>();
  const redirectTo = routeParams?.redirectTo;

  // ─────────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isExpoRouterEnabled) {
      if (router?.canGoBack()) {
        router.back();
        return;
      }

      router?.navigate('/scan-at-checkout');

      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation?.navigate('ScanAtCheckout');
  }, [isExpoRouterEnabled, router, navigation]);

  const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

  useLayoutEffect(() => {
    setOptions?.({ headerShown: true, presentation: 'modal' });
  }, [setOptions]);

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="payments" withoutFooter />;
  }

  return (
    <PageWrapper
      safeAreaEdges={safeAreaAndroid}
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      withHeaderTitle
      withoutFooter
    >
      <DisplayText sizeMatch={['24']}>
        <FormattedMessage {...messages.title} />
      </DisplayText>

      <StripeCustomerSheet visible redirectTo={redirectTo} onClose={handleClose} />
    </PageWrapper>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payments',
    description: 'Payments Screen | Title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const safeAreaAndroid: SafeAreaViewProps['edges'] = Platform.select({
  android: ['top'],
  default: [],
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['4'],
  },
  content: {
    flex: 1,
    gap: theme.spacing['4'],
  },
});
